import { create } from "zustand";
import * as Sentry from "@sentry/react";
import { me } from "../api/auth.api";
import useWalletStore from "./wallet.store";
import useSubscriptionStore from "./subscription.store";

// 2026-06-18 — auth-change store reset. Called from setAuth + clearAuth.
// Without this, a stale wallet balance / mySubscription from the
// previous account's session briefly flashes on the new account's
// first render before the next fetch resolves. Caused the Adhish
// "Intermediate + 700 credits" display-confusion bug reported
// 2026-06-17 where the SPA showed a different account's state.
function resetDownstreamStores() {
  try { useWalletStore.getState().reset(); } catch { /* ignore */ }
  try { useSubscriptionStore.getState().reset(); } catch { /* ignore */ }
}

// Logout flag — set by clearAuth(), checked by initAuth(). Prevents the
// "logout doesn't stick" bug where a stale session cookie (CloudFront,
// cookie-domain mismatch) survives logout, /me succeeds on the next
// visit, and RootGate auto-redirects the just-logged-out user back to
// the dashboard. Cleared on every successful setAuth(login/signup).
const LOGGED_OUT_FLAG = "zcd_logged_out_v1";

function isLoggedOut() {
  try { return typeof localStorage !== "undefined" && localStorage.getItem(LOGGED_OUT_FLAG) === "1"; }
  catch { return false; }
}
function markLoggedOut() {
  try { localStorage.setItem(LOGGED_OUT_FLAG, "1"); } catch { /* ignore */ }
}
function clearLoggedOut() {
  try { localStorage.removeItem(LOGGED_OUT_FLAG); } catch { /* ignore */ }
}

const setSentryUser = (user) => {
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({
    id: user.id ? String(user.id) : undefined,
    username: user.username,
    email: user.email,
    account_type: user.account_type_v2 || user.account_type,
    role: user.account_user_role || user.role,
  });
};

const useAuthStore = create((set) => ({
  user: null,
  isAuthenticated: false,
  loading: true,

  // New fields for account info
  accountType: null, // "solo" or "organization"
  organizationName: null,
  organizationId: null,
  role: null, // "OWNER", "ADMIN", "MEMBER"

  initAuth: async () => {
    // If the user just logged out, do not silently re-auth from a stale
    // session cookie. They must explicitly log in again to clear the flag.
    if (isLoggedOut()) {
      setSentryUser(null);
      set({
        user: null,
        isAuthenticated: false,
        accountType: null,
        organizationName: null,
        organizationId: null,
        role: null,
        loading: false,
      });
      return;
    }
    try {
      const res = await me();
      setSentryUser(res.data);
      set({
        user: res.data,
        isAuthenticated: true,
        accountType: res.data.account_type || null,
        organizationName: res.data.organization_name || null,
        organizationId: res.data.organization_id || null,
        role: res.data.role || null,
      });
    } catch (err) {
      setSentryUser(null);
      set({
        user: null,
        isAuthenticated: false,
        accountType: null,
        organizationName: null,
        organizationId: null,
        role: null,
      });
    } finally {
      set({ loading: false });
    }
  },

  setAuth: (user) => {
    // Successful login/signup → clear the "just logged out" flag so the
    // next initAuth probes /me normally.
    clearLoggedOut();
    setSentryUser(user);
    // Wipe any stale wallet / subscription state from the previous
    // session before the new account starts fetching its own data.
    resetDownstreamStores();
    set({
      user,
      isAuthenticated: true,
      accountType: user.account_type || null,
      organizationName: user.organization_name || null,
      organizationId: user.organization_id || null,
      role: user.role || null,
    });
  },

  clearAuth: () => {
    // Persist the logout decision across reloads. initAuth checks this
    // BEFORE calling /me, so even if the session cookie outlives logout
    // (CloudFront, cookie-domain mismatch), the user is treated as
    // logged-out until they explicitly sign in again.
    markLoggedOut();
    setSentryUser(null);
    resetDownstreamStores();
    set({
      user: null,
      isAuthenticated: false,
      accountType: null,
      organizationName: null,
      organizationId: null,
      role: null,
    });
  },
}));

export default useAuthStore;
